import {
  ProductCardChangeEvents,
  SelectStates,
  StockStatuses
} from '@dcxwam/dcx-wam-ui-foundations'

import {
  COLOR_PROPERTY,
  MAX_COLORS_PER_VIEW,
  buildMultibuySelectItems,
  getColorSelector,
  getCurrentAttributeSelectorValues,
  getMultibuyDefaultValue,
  getStockLevelStatus,
  showProduct,
  sortVariantsByStock,
  stockStatusMap
} from '../utils'
import { ProductFamilyEnum, type GroupedItem } from '../models'
import { PRODUCT_CARD_ID_PREFIX } from '../utils/constants'
import type { ProductDataVariant } from '../../../../services/product/product-data'
import type { Stocks } from '../utils/stock'
import type { FilterStashType, HistoryFilter } from '../utils/filter'
import type { ShopConfig } from '../../common/config'
import type { FormatPriceOptions } from '../../../../utils/format-price'
import type { BuildVariantDataParams } from './variant-data-builder'

type QuantitiesSelectedType = {
  [key: string]: number
}

export interface ProductItemContext {
  stocks: Stocks
  filtersStash: FilterStashType
  historyFilters: HistoryFilter[]
  general: {
    colorDotsVisibility?: boolean
    dataLayerListName?: string
  }
  attributeSelectorsProp: Array<{
    name: string
    label: string
    showPrice: boolean
  }>
  shopConfig: ShopConfig
  pageLocale: string
  formatPriceOptions?: FormatPriceOptions
  isAutoDeliveryEnabled: boolean
  multibuySelections: Record<string, number>
  activeVariantByCard: Record<string, string>
  setActiveVariantByCard: (
    fn: (prev: Record<string, string>) => Record<string, string>
  ) => void
  setMultibuySelections: (
    fn: (prev: QuantitiesSelectedType) => QuantitiesSelectedType
  ) => void
  setUpdatedStocks: (gcodes: string[]) => void
  dataGcodes: string[]
  sortedItemsByPrice: GroupedItem[] | undefined
  onVariantColorChange: (
    variant: Partial<ProductDataVariant>,
    meta: { list: string; position: number }
  ) => void
  buildVariantData: (params: BuildVariantDataParams) => unknown
}

export const buildProductItem = (
  item: GroupedItem,
  order: number,
  ctx: ProductItemContext
) => {
  const { variants } = item
  const position = order + 1
  const sortedVariantsByStock = variants
    ?.slice()
    ?.sort(sortVariantsByStock(ctx.stocks))

  const augmentedVariants = sortedVariantsByStock?.map((v) => ({
    ...v,
    [COLOR_PROPERTY]: v.nuance?.code
  }))

  const isProductSet = sortedVariantsByStock?.some(
    (v) => v.productFamily === ProductFamilyEnum.ProductSet
  )

  const firstVariant = sortedVariantsByStock?.[0]
  const cardId = `${PRODUCT_CARD_ID_PREFIX}${position}`
  const trackedCode = ctx.activeVariantByCard[cardId]
  const activeVariant = trackedCode
    ? (sortedVariantsByStock?.find(
        (v) => v.globalArticleCode === trackedCode
      ) ?? firstVariant)
    : firstVariant
  const multiBuyPrice = activeVariant?.price?.multiBuyPrice
  const variantCode = activeVariant?.globalArticleCode ?? ''
  const statusKey = getStockLevelStatus(activeVariant, ctx.stocks)
  const isOutOfStock = statusKey
    ? stockStatusMap[statusKey] === StockStatuses.OutOfStock
    : false

  const activeVariantStockLevel = variantCode
    ? ctx.stocks[variantCode]?.stockLevel
    : undefined

  const multibuyItems =
    !isProductSet && multiBuyPrice?.length
      ? buildMultibuySelectItems({
          variant: activeVariant as ProductDataVariant,
          shopConfig: ctx.shopConfig,
          pageLocale: ctx.pageLocale,
          stockLevel: activeVariantStockLevel,
          formatPriceOptions: ctx.formatPriceOptions
        })
      : undefined

  const multibuyDefaultValue = getMultibuyDefaultValue(
    multiBuyPrice,
    ctx.shopConfig?.multibuyBestValueToggle,
    activeVariantStockLevel
  )

  const currentMultibuyValue =
    ctx.multibuySelections[variantCode] != null
      ? String(ctx.multibuySelections[variantCode])
      : multibuyDefaultValue

  const multibuySelectProps =
    multibuyItems?.length && !isProductSet
      ? {
          items: multibuyItems,
          value: currentMultibuyValue,
          onChange: (value: string) => {
            ctx.setMultibuySelections((prev: QuantitiesSelectedType) => ({
              ...prev,
              [variantCode]: Number(value)
            }))
          },
          ...(isOutOfStock
            ? { selectProps: { state: SelectStates.Disabled } }
            : {}),
          ...(ctx.shopConfig?.multibuyGroupTitle
            ? {
                'aria-label': ctx.shopConfig.multibuyGroupTitle
              }
            : {})
        }
      : undefined

  return {
    srOnly: !showProduct(sortedVariantsByStock, ctx.filtersStash),
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    id: `${PRODUCT_CARD_ID_PREFIX}${position}`,
    'data-position': position,
    isAutoDelivery: ctx.isAutoDeliveryEnabled,
    ...(!isProductSet && {
      colorSelector: getColorSelector({
        variants: sortedVariantsByStock,
        stocks: ctx.stocks,
        historyFilters: ctx.historyFilters,
        dotsVisibility: ctx.general?.colorDotsVisibility ?? false,
        maxColorsPerView: MAX_COLORS_PER_VIEW
      })
    }),
    currentAttributeSelectorValues: getCurrentAttributeSelectorValues(
      ctx.attributeSelectorsProp,
      sortedVariantsByStock,
      ctx.historyFilters
    ),
    ...(multibuySelectProps ? { multibuySelectProps } : {}),
    variants: augmentedVariants?.map((v, idx, list) =>
      ctx.buildVariantData({
        variant: v as ProductDataVariant,
        variantIndex: idx,
        variantList: list as ProductDataVariant[],
        position
      })
    ),
    onChange(
      e:
        | React.MouseEvent<HTMLElement>
        | React.ChangeEvent<EventTarget | HTMLInputElement>,
      values: { type?: ProductCardChangeEvents },
      variant: unknown
    ) {
      ctx.setUpdatedStocks(ctx.dataGcodes)
      if (
        values?.type === ProductCardChangeEvents.Color &&
        ctx.sortedItemsByPrice
      ) {
        const currentVariant = variant as { code?: string }
        if (currentVariant?.code) {
          ctx.setActiveVariantByCard((prev) => ({
            ...prev,
            [cardId]: currentVariant.code as string
          }))
        }
        const index = position - 1
        const itemAtIndex = ctx.sortedItemsByPrice[index]
        if (itemAtIndex) {
          const [matchedVariant] = itemAtIndex.variants.filter(
            ({ globalArticleCode }: { globalArticleCode?: string }) =>
              currentVariant?.code === globalArticleCode
          )
          if (matchedVariant) {
            ctx.onVariantColorChange(matchedVariant, {
              list: ctx.general?.dataLayerListName ?? '',
              position
            })
          }
        }
      }
    }
  }
}
